/**
 * The Sidor view: space switcher, page tree with drag-and-drop, the lazy-loaded editor with
 * autosave and optimistic locking, backlinks and history.
 *
 * This view owns all saving. The title field and the document share one revision counter, so a
 * single serialised save queue prevents them from conflicting with each other.
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ApiError,
  api,
  type Backlink,
  type EditorEntry,
  type PageDetail,
  type Space,
  type Suggestion,
  type TreePage,
} from "../../api";
import type { WikiLinkAttrs } from "../../editor/wikilink";
import { Dialog } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { FavoriteStar } from "../../components/FavoriteStar";
import { Button, Card, Field, Num } from "../../components/primitives";
import { SkeletonText } from "../../components/Skeleton";
import { useToast } from "../../components/toast";
import { HistoryPanel } from "./HistoryPanel";
import { PageTree, type DropIntent } from "./PageTree";

const PageEditor = lazy(() => import("../../editor/PageEditor"));
const PdfViewer = lazy(() => import("../../editor/PdfViewer"));

const AUTOSAVE_DELAY_MS = 900;

type SaveState = "saved" | "pending" | "saving" | "conflict" | "error";

const SAVE_LABELS: Record<SaveState, string> = {
  saved: "Sparad",
  pending: "Ändrad …",
  saving: "Sparar …",
  conflict: "Konflikt",
  error: "Kunde inte spara",
};

export function SidorPage() {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const showToast = useToast();

  const [spaces, setSpaces] = useState<Space[] | null>(null);
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [tree, setTree] = useState<TreePage[]>([]);
  const [page, setPage] = useState<PageDetail | null>(null);
  const [backlinks, setBacklinks] = useState<Backlink[]>([]);

  // WP0.13: the tree and the backlinks fold away so the writing surface gets the room; both
  // choices stick across sessions. localStorage over the API — this is a per-device preference.
  const [treeOpen, setTreeOpen] = useState(
    () => localStorage.getItem("noteshelf.sidor.trad") !== "dolt",
  );
  const toggleTree = () =>
    setTreeOpen((open) => {
      localStorage.setItem("noteshelf.sidor.trad", open ? "dolt" : "visat");
      return !open;
    });
  const [backlinksOpen, setBacklinksOpen] = useState(
    () => localStorage.getItem("noteshelf.sidor.lankar") !== "dolt",
  );
  const toggleBacklinks = () =>
    setBacklinksOpen((open) => {
      localStorage.setItem("noteshelf.sidor.lankar", open ? "dolt" : "visat");
      return !open;
    });
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [showHistory, setShowHistory] = useState(false);
  const [historyText, setHistoryText] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  // Set when creating a subpage under the open page; null = a root page. Subpages have lived
  // in the model since WP0.4 (parent_id + drag-and-drop) — this makes them discoverable.
  const [createParentId, setCreateParentId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [similar, setSimilar] = useState<Suggestion[]>([]);
  const [trashOpen, setTrashOpen] = useState(false);
  const [archivedList, setArchivedList] = useState<
    { id: string; title: string; deleted_at: string }[] | null
  >(null);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [protectionOpen, setProtectionOpen] = useState(false);

  // Protected pages (WP0.8): the server enforces; this mirrors it in the interface.
  const canEdit = page?.protection?.can_edit ?? true;
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [pdf, setPdf] = useState<{ url: string; filename: string } | null>(null);

  // One serialised save pipeline for title and content, sharing the revision counter.
  const revisionRef = useRef(0);
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingDoc = useRef<Record<string, unknown> | null>(null);
  const conflictRevision = useRef<number | null>(null);
  const timerRef = useRef<number | undefined>(undefined);

  const refreshTree = useCallback(async (space: string) => {
    setTree(await api.pageTree(space));
  }, []);

  // Spaces on mount.
  useEffect(() => {
    void api.spaces().then((loaded) => {
      setSpaces(loaded);
      setSpaceId((current) => current ?? loaded[0]?.id ?? null);
    });
  }, []);

  // Tree when the space changes.
  useEffect(() => {
    if (spaceId) {
      void refreshTree(spaceId);
    }
  }, [spaceId, refreshTree]);

  // The page when the route changes.
  useEffect(() => {
    if (!pageId) {
      setPage(null);
      return;
    }
    let cancelled = false;
    void api
      .page(pageId)
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        setPage(loaded);
        setSpaceId(loaded.space_id);
        revisionRef.current = loaded.current_revision;
        conflictRevision.current = null;
        pendingDoc.current = null;
        setSaveState("saved");
        setEditorEpoch((epoch) => epoch + 1);
        void api.backlinks(loaded.id).then((links) => !cancelled && setBacklinks(links));
      })
      .catch(() => {
        showToast({ message: "Sidan kunde inte öppnas.", tone: "fail" });
        navigate("/sidor", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [pageId, navigate, showToast]);

  const enqueueSave = useCallback(
    (body: { title?: string; content?: Record<string, unknown> }) => {
      if (!pageId) {
        return;
      }
      setSaveState("saving");
      queueRef.current = queueRef.current.then(async () => {
        try {
          const saved = await api.savePage(pageId, {
            base_revision: revisionRef.current,
            ...body,
          });
          revisionRef.current = saved.current_revision;
          setSaveState("saved");
        } catch (error) {
          if (error instanceof ApiError && error.status === 409) {
            conflictRevision.current = Number(error.body.current_revision);
            setSaveState("conflict");
          } else {
            setSaveState("error");
          }
        }
      });
    },
    [pageId],
  );

  const onDocChange = useCallback(
    (doc: Record<string, unknown>) => {
      pendingDoc.current = doc;
      setSaveState("pending");
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        if (pendingDoc.current) {
          enqueueSave({ content: pendingDoc.current });
        }
      }, AUTOSAVE_DELAY_MS);
    },
    [enqueueSave],
  );

  // Ctrl+S: flush now instead of the browser's save dialog.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        window.clearTimeout(timerRef.current);
        if (pendingDoc.current) {
          enqueueSave({ content: pendingDoc.current });
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enqueueSave]);

  const onTitleChange = useCallback(
    (title: string) => {
      setPage((current) => (current ? { ...current, title } : current));
      setTree((current) =>
        current.map((node) => (node.id === pageId ? { ...node, title } : node)),
      );
      setSaveState("pending");
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        if (title.trim()) {
          enqueueSave({ title: title.trim() });
        }
      }, AUTOSAVE_DELAY_MS);
    },
    [enqueueSave, pageId],
  );

  const reloadTheirs = useCallback(async () => {
    if (!pageId) {
      return;
    }
    const fresh = await api.page(pageId);
    setPage(fresh);
    revisionRef.current = fresh.current_revision;
    conflictRevision.current = null;
    pendingDoc.current = null;
    setSaveState("saved");
    setEditorEpoch((epoch) => epoch + 1);
  }, [pageId]);

  const overwriteWithMine = useCallback(() => {
    if (conflictRevision.current === null) {
      return;
    }
    revisionRef.current = conflictRevision.current;
    conflictRevision.current = null;
    const body: { title?: string; content?: Record<string, unknown> } = {};
    if (page?.title) {
      body.title = page.title;
    }
    if (pendingDoc.current) {
      body.content = pendingDoc.current;
    }
    enqueueSave(body);
  }, [enqueueSave, page?.title]);

  const createPage = useCallback(async () => {
    if (!spaceId || !newTitle.trim()) {
      return;
    }
    const created = await api.createPage({
      space_id: spaceId,
      title: newTitle.trim(),
      parent_id: createParentId,
      template_id: templateId || null,
    });
    setCreateOpen(false);
    setNewTitle("");
    setCreateParentId(null);
    setTemplateId("");
    setSimilar([]);
    await refreshTree(spaceId);
    navigate(`/sidor/${created.id}`);
  }, [spaceId, newTitle, createParentId, templateId, refreshTree, navigate]);

  // Templates for the create dialog, fetched when it opens.
  useEffect(() => {
    if (createOpen) {
      void api.pageTemplates().then(setTemplates);
    }
  }, [createOpen]);

  // Duplicate guard: while naming a new page, show existing pages with similar titles.
  useEffect(() => {
    if (!createOpen || !newTitle.trim() || !spaceId) {
      setSimilar([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void api
        .suggest(newTitle.trim(), spaceId)
        .then((found) => setSimilar(found.filter((s) => s.kind === "page").slice(0, 4)));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [createOpen, newTitle, spaceId]);

  // Archiving is reversible since the restore endpoint exists, so per the house rule it acts
  // immediately and offers undo — no confirmation dialog.
  const archive = useCallback(async () => {
    if (!pageId || !spaceId) {
      return;
    }
    const result = await api.archivePage(pageId);
    await refreshTree(spaceId);
    navigate("/sidor");
    showToast({
      message: `${result.archived} ${result.archived === 1 ? "sida" : "sidor"} arkiverades.`,
      undo: () => {
        void api.restorePage(pageId).then(async () => {
          await refreshTree(spaceId);
          navigate(`/sidor/${pageId}`);
        });
      },
    });
  }, [pageId, spaceId, refreshTree, navigate, showToast]);

  const openTrash = useCallback(async () => {
    if (!spaceId) {
      return;
    }
    setTrashOpen(true);
    setArchivedList(await api.archivedPages(spaceId));
  }, [spaceId]);

  const restoreFromTrash = useCallback(
    async (id: string) => {
      if (!spaceId) {
        return;
      }
      const result = await api.restorePage(id);
      setArchivedList(await api.archivedPages(spaceId));
      await refreshTree(spaceId);
      showToast({
        message: `${result.restored} ${result.restored === 1 ? "sida" : "sidor"} återställdes.`,
      });
    },
    [spaceId, refreshTree, showToast],
  );

  const saveAsTemplate = useCallback(async () => {
    if (!pageId || !templateName.trim()) {
      return;
    }
    await api.createPageTemplate({ name: templateName.trim(), page_id: pageId });
    setSaveTemplateOpen(false);
    setTemplateName("");
    showToast({ message: `Mallen ”${templateName.trim()}” skapades.` });
  }, [pageId, templateName, showToast]);

  const fixEdition = useCallback(async () => {
    if (!pageId) {
      return;
    }
    const edition = await api.fixEdition(pageId);
    setPage((current) => (current ? { ...current, latest_edition: edition } : current));
    showToast({ message: `Utgåva ${edition.edition} fastställd.` });
  }, [pageId, showToast]);

  const setReview = useCallback(
    async (value: string | null) => {
      if (!pageId) {
        return;
      }
      const updated = await api.setReview(pageId, value);
      setPage((current) =>
        current ? { ...current, review_due_at: updated.review_due_at } : current,
      );
    },
    [pageId],
  );

  // Sensitivity (WP1.1): reversible, so it acts immediately with an undo toast.
  const markSensitive = useCallback(
    async (sensitive: boolean) => {
      if (!pageId) {
        return;
      }
      try {
        const updated = await api.setSensitivity(pageId, sensitive);
        setPage((current) => (current ? { ...current, sensitivity: updated } : current));
        showToast({
          message: sensitive
            ? "Sidan är märkt känslig — den behandlas aldrig av molnmodeller."
            : "Känslighetsmärkningen hävdes.",
          undo: async () => {
            const reverted = await api.setSensitivity(pageId, !sensitive);
            setPage((current) => (current ? { ...current, sensitivity: reverted } : current));
          },
        });
      } catch (error) {
        showToast({
          message:
            error instanceof ApiError ? error.message : "Känslighetsmärkningen kunde inte sparas.",
          tone: "fail",
        });
      }
    },
    [pageId, showToast],
  );

  const onDrop = useCallback(
    async (draggedId: string, intent: DropIntent) => {
      if (!spaceId) {
        return;
      }
      const target = tree.find((node) => node.id === intent.targetId);
      if (!target) {
        return;
      }
      const parentId = intent.placement === "inside" ? target.id : target.parent_id;
      const position =
        intent.placement === "inside"
          ? 0
          : intent.placement === "before"
            ? target.position
            : target.position + 1;
      try {
        await api.movePage(draggedId, { parent_id: parentId, position });
      } catch (error) {
        showToast({
          message: error instanceof ApiError ? error.message : "Sidan kunde inte flyttas.",
          tone: "fail",
        });
      }
      await refreshTree(spaceId);
    },
    [spaceId, tree, refreshTree, showToast],
  );

  const openLink = useCallback(
    async (attrs: WikiLinkAttrs) => {
      if (attrs.pageId) {
        navigate(`/sidor/${attrs.pageId}`);
        return;
      }
      // An unresolved link: offer to create the page it names.
      if (!spaceId) {
        return;
      }
      const created = await api.createPage({ space_id: spaceId, title: attrs.title });
      showToast({ message: `Sidan ”${attrs.title}” skapades.` });
      await refreshTree(spaceId);
      navigate(`/sidor/${created.id}`);
    },
    [navigate, spaceId, refreshTree, showToast],
  );

  if (spaces === null) {
    return <SkeletonText lines={4} />;
  }

  return (
    <div
      className={`grid gap-s6 ${treeOpen ? "lg:grid-cols-[240px_1fr]" : "lg:grid-cols-[auto_1fr]"}`}
    >
      {!treeOpen && (
        <div className="content-start">
          <Button variant="ghost" title="Visa sidträdet" onClick={toggleTree}>
            ⟩
          </Button>
        </div>
      )}
      {/* Tree column: min-w-0 so the fixed 240px track constrains the children instead of
          the children (a select sized by its widest option) overflowing onto the editor. */}
      {treeOpen && (
      <div className="grid min-w-0 content-start gap-s5">
        <label className="grid min-w-0 gap-s2">
          <span className="nb-overline border-0 pb-0">Utrymme</span>
          <select
            className="nb-field w-full min-w-0"
            value={spaceId ?? ""}
            onChange={(event) => {
              setSpaceId(event.target.value);
              navigate("/sidor");
            }}
          >
            {spaces.map((space) => (
              <option key={space.id} value={space.id}>
                {space.kind === "private" ? `${space.name} (privat)` : space.name}
              </option>
            ))}
          </select>
        </label>

        <Button
          variant="cta"
          onClick={() => {
            setCreateParentId(null);
            setCreateOpen(true);
          }}
        >
          Ny sida
        </Button>

        {tree.length === 0 ? (
          <p className="text-small text-muted">Inga sidor här ännu.</p>
        ) : (
          <PageTree
            pages={tree}
            activePageId={pageId ?? null}
            onOpen={(id) => navigate(`/sidor/${id}`)}
            onDrop={onDrop}
          />
        )}

        <div className="flex flex-wrap gap-s2">
          <Button variant="ghost" onClick={() => void openTrash()}>
            Arkiverade sidor …
          </Button>
          <Button variant="ghost" title="Dölj sidträdet" onClick={toggleTree}>
            ⟨ Dölj
          </Button>
        </div>
      </div>
      )}

      {/* Editor column */}
      {!page ? (
        <EmptyState
          title="Välj en sida"
          description="Öppna en sida i trädet till vänster, eller skapa en ny. Markdown-genvägar och [[länkar]] fungerar direkt i editorn."
          action={undefined}
        />
      ) : (
        <div className="grid content-start gap-s5">
          <Card>
            <div className="flex flex-wrap items-center gap-s4">
              <input
                aria-label="Sidtitel"
                className="min-w-0 flex-1 border-0 bg-transparent text-h2 font-bold text-ink outline-none"
                value={page.title}
                readOnly={!canEdit}
                onChange={(event) => canEdit && onTitleChange(event.target.value)}
              />
              <FavoriteStar key={page.id} kind="page" targetId={page.id} />
              <span
                aria-live="polite"
                className={`nb-num text-overline uppercase ${
                  saveState === "error" || saveState === "conflict"
                    ? "text-fail-text"
                    : "text-muted"
                }`}
              >
                {SAVE_LABELS[saveState]} · <Num>v{revisionRef.current}</Num>
              </span>
              <Button
                variant="ghost"
                onClick={() => {
                  const opening = !showHistory;
                  setShowHistory(opening);
                  if (opening && pageId) {
                    // Fresh text_cache so the diff compares against what is actually saved.
                    void api.page(pageId).then((fresh) => setHistoryText(fresh.text_cache));
                  }
                }}
              >
                Historik
              </Button>
              {canEdit && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setCreateParentId(page.id);
                    setCreateOpen(true);
                  }}
                >
                  Ny undersida
                </Button>
              )}
              <Button variant="ghost" onClick={() => setSaveTemplateOpen(true)}>
                Spara som mall
              </Button>
              {canEdit && (
                <Button variant="ghost" onClick={() => void fixEdition()}>
                  Fastställ utgåva
                </Button>
              )}
              {page.protection?.can_manage && (
                <Button variant="ghost" onClick={() => setProtectionOpen(true)}>
                  {page.protection.own ? "Hantera skydd" : "Skydda"}
                </Button>
              )}
              {page.sensitivity?.can_manage && !page.sensitivity.sensitive && (
                <Button variant="ghost" onClick={() => void markSensitive(true)}>
                  Märk känslig
                </Button>
              )}
              {page.sensitivity?.own && (
                <Button variant="ghost" onClick={() => void markSensitive(false)}>
                  Häv känslig
                </Button>
              )}
              {canEdit && (
                <Button variant="ghost" onClick={() => void archive()}>
                  Arkivera
                </Button>
              )}
            </div>

            {page.protection?.protected && (
              <div className="mt-s4 flex flex-wrap items-center gap-s4 rounded-card bg-surface-2 px-s5 py-s3">
                <span className="text-small text-ink">
                  🔒 Skyddad sida
                  {page.protection.inherited_from_title
                    ? ` — ärvt från ”${page.protection.inherited_from_title}”`
                    : ""}
                  {" · redigeras av "}
                  {page.protection.editors.map((e) => e.display_name).join(", ") ||
                    "administratörer"}
                </span>
                {!canEdit && (
                  <span className="text-small text-muted">Du kan läsa men inte ändra.</span>
                )}
              </div>
            )}

            {page.sensitivity?.sensitive && (
              <div className="mt-s4 flex flex-wrap items-center gap-s4 rounded-card bg-surface-2 px-s5 py-s3">
                <span className="text-small text-ink">
                  🛡 Känslig sida
                  {page.sensitivity.inherited_from_title
                    ? ` — ärvt från ”${page.sensitivity.inherited_from_title}”`
                    : ""}
                  {" · behandlas aldrig av molnmodeller"}
                </span>
              </div>
            )}

            <GovernanceBar page={page} canEdit={canEdit} onSetReview={(v) => void setReview(v)} />

            {saveState === "conflict" && (
              <div
                role="alert"
                className="mt-s5 flex flex-wrap items-center gap-s4 rounded-card bg-warn-bg px-s5 py-s4"
              >
                <p className="flex-1 text-body text-warn-text">
                  Sidan har ändrats av någon annan medan du skrev. Dina ändringar är kvar här —
                  välj vad som gäller.
                </p>
                <Button onClick={() => void reloadTheirs()}>Ladda deras version</Button>
                <Button variant="primary" onClick={overwriteWithMine}>
                  Skriv över med min
                </Button>
              </div>
            )}

            <div className="mt-s5">
              <Suspense fallback={<SkeletonText lines={6} />}>
                <PageEditor
                  key={`${page.id}:${editorEpoch}:${canEdit}`}
                  initialContent={page.content}
                  spaceId={page.space_id}
                  editable={canEdit}
                  onDocChange={onDocChange}
                  onNavigateToLink={(attrs) => void openLink(attrs)}
                  onOpenPdf={(url, filename) => setPdf({ url, filename })}
                  report={(message, tone) => showToast({ message, tone: tone ?? "info" })}
                />
              </Suspense>
            </div>
          </Card>

          {showHistory && (
            <Card>
              <h2 className="nb-overline mb-s5">Historik</h2>
              <HistoryPanel pageId={page.id} currentText={historyText} />
            </Card>
          )}

          <Card sunken>
            <button
              type="button"
              className="nb-overline mb-s4 flex w-full items-center justify-between border-0 bg-transparent p-0 text-left"
              aria-expanded={backlinksOpen}
              onClick={toggleBacklinks}
            >
              <span>Länkar hit{backlinks.length > 0 ? ` (${backlinks.length})` : ""}</span>
              <span aria-hidden>{backlinksOpen ? "⌃" : "⌄"}</span>
            </button>
            {backlinksOpen &&
              (backlinks.length === 0 ? (
                <p className="text-small text-muted">Inga sidor länkar hit ännu.</p>
              ) : (
                <ul className="flex flex-wrap gap-s4">
                  {backlinks.map((link) => (
                    <li key={link.page_id}>
                      <button
                        type="button"
                        className="nb-link border-0 bg-transparent p-0"
                        onClick={() => navigate(`/sidor/${link.page_id}`)}
                      >
                        {link.title}
                      </button>
                    </li>
                  ))}
                </ul>
              ))}
          </Card>
        </div>
      )}

      {/* Create page */}
      <Dialog
        open={createOpen}
        title={createParentId ? `Ny undersida under ”${page?.title ?? "sidan"}”` : "Ny sida"}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button onClick={() => setCreateOpen(false)}>Avbryt</Button>
            <Button variant="primary" onClick={() => void createPage()} disabled={!newTitle.trim()}>
              Skapa
            </Button>
          </>
        }
      >
        <form
          className="grid gap-s5"
          onSubmit={(event) => {
            event.preventDefault();
            void createPage();
          }}
        >
          <Field
            label="Titel"
            value={newTitle}
            onChange={(event) => setNewTitle(event.target.value)}
            autoFocus
          />
          {similar.length > 0 && (
            <div className="rounded-card bg-warn-bg px-s5 py-s4">
              <p className="text-small text-warn-text">Liknande sidor finns redan:</p>
              <ul className="mt-s2 flex flex-wrap gap-s4">
                {similar.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className="nb-link border-0 bg-transparent p-0 text-small"
                      onClick={() => {
                        setCreateOpen(false);
                        navigate(`/sidor/${s.id}`);
                      }}
                    >
                      {s.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {templates.length > 0 && (
            <label className="grid gap-s2">
              <span className="nb-overline border-0 pb-0">Mall</span>
              <select
                className="nb-field w-full min-w-0"
                value={templateId}
                onChange={(event) => setTemplateId(event.target.value)}
              >
                <option value="">Tom sida</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </form>
      </Dialog>

      {/* Save the open page as a template */}
      <Dialog
        open={saveTemplateOpen}
        title="Spara som mall"
        subtitle="En kopia av sidans nuvarande innehåll"
        onClose={() => setSaveTemplateOpen(false)}
        footer={
          <>
            <Button onClick={() => setSaveTemplateOpen(false)}>Avbryt</Button>
            <Button
              variant="primary"
              onClick={() => void saveAsTemplate()}
              disabled={!templateName.trim()}
            >
              Spara mall
            </Button>
          </>
        }
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void saveAsTemplate();
          }}
        >
          <Field
            label="Mallens namn"
            value={templateName}
            onChange={(event) => setTemplateName(event.target.value)}
            help="Mallen dyker upp som val i Ny sida-dialogen."
            autoFocus
          />
        </form>
      </Dialog>

      {/* Protection management (WP0.8) */}
      {page && (
        <ProtectionDialog
          open={protectionOpen}
          page={page}
          onClose={() => setProtectionOpen(false)}
          onChanged={(protection) => {
            setPage((current) => (current ? { ...current, protection } : current));
            setProtectionOpen(false);
          }}
        />
      )}

      {/* The wastebasket: archived pages with restore */}
      <Dialog
        open={trashOpen}
        title="Arkiverade sidor"
        subtitle="Återställning tar tillbaka hela det arkiverade trädet"
        onClose={() => setTrashOpen(false)}
      >
        {archivedList === null ? (
          <SkeletonText lines={3} />
        ) : archivedList.length === 0 ? (
          <p className="text-body text-muted">Inga arkiverade sidor i det här utrymmet.</p>
        ) : (
          <ul className="grid gap-s2">
            {archivedList.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-s4 rounded-control px-s3 py-s2 hover:bg-surface-2"
              >
                <span className="min-w-0 flex-1 truncate text-body text-ink">{item.title}</span>
                <Button variant="ghost" onClick={() => void restoreFromTrash(item.id)}>
                  Återställ
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Dialog>

      {/* In-app PDF reading; annotation and approval come with WP4.5. */}
      <Dialog
        open={pdf !== null}
        title={pdf?.filename ?? "PDF"}
        onClose={() => setPdf(null)}
        wide
      >
        {pdf && (
          <Suspense fallback={<SkeletonText lines={6} />}>
            <PdfViewer url={pdf.url} filename={pdf.filename} />
          </Suspense>
        )}
      </Dialog>

    </div>
  );
}

/**
 * Manage protection (WP0.8): toggle the shield and appoint editors. Policy: admin and the
 * page's creator manage; the creator and the protector always count as editors.
 */
function ProtectionDialog({
  open,
  page,
  onClose,
  onChanged,
}: {
  open: boolean;
  page: PageDetail;
  onClose: () => void;
  onChanged: (protection: NonNullable<PageDetail["protection"]>) => void;
}) {
  const [users, setUsers] = useState<EditorEntry[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      void api.users().then(setUsers);
      setSelected(new Set(page.protection?.editors.map((e) => e.id) ?? []));
    }
  }, [open, page.protection]);

  const apply = async (protect: boolean) => {
    const protection = await api.setProtection(page.id, {
      protected: protect,
      editor_ids: [...selected],
    });
    onChanged(protection);
  };

  return (
    <Dialog
      open={open}
      title={page.protection?.own ? "Hantera skydd" : "Skydda sidan"}
      subtitle="Alla med utrymmesåtkomst kan läsa; endast redigerarna ändrar"
      onClose={onClose}
      footer={
        <>
          {page.protection?.own && (
            <Button onClick={() => void apply(false)}>Häv skyddet</Button>
          )}
          <Button variant="primary" onClick={() => void apply(true)}>
            {page.protection?.own ? "Spara redigerare" : "Skydda"}
          </Button>
        </>
      }
    >
      <div className="grid gap-s4">
        <p className="text-small text-muted">
          Skyddet ärvs av alla undersidor. Sidans skapare och den som skyddar ingår alltid bland
          redigerarna; administratörer kan alltid ändra.
        </p>
        <ul className="grid max-h-[40vh] gap-s2 overflow-y-auto">
          {users.map((candidate) => (
            <li key={candidate.id}>
              <label className="flex items-center gap-s3 rounded-control px-s3 py-s2 text-body text-ink hover:bg-surface-2">
                <input
                  type="checkbox"
                  checked={selected.has(candidate.id)}
                  style={{ accentColor: "var(--drop)" }}
                  onChange={(event) =>
                    setSelected((current) => {
                      const next = new Set(current);
                      if (event.target.checked) {
                        next.add(candidate.id);
                      } else {
                        next.delete(candidate.id);
                      }
                      return next;
                    })
                  }
                />
                {candidate.display_name}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}

/**
 * The governance strip under the title (WP0.9): shows the fixed edition and warns when the
 * draft has moved past it, and lets a reviewer set or clear the "review by" date.
 */
function GovernanceBar({
  page,
  canEdit,
  onSetReview,
}: {
  page: PageDetail;
  canEdit: boolean;
  onSetReview: (value: string | null) => void;
}) {
  const edition = page.latest_edition;
  const reviewDate = page.review_due_at ? page.review_due_at.slice(0, 10) : "";
  const overdue = page.review_due_at ? new Date(page.review_due_at) < new Date() : false;

  if (!edition && !page.review_due_at) {
    if (!canEdit) {
      return null;
    }
    // Nothing governed yet — offer just the review-date control, unobtrusively.
    return (
      <div className="mt-s4 flex items-center gap-s3 text-small text-muted">
        <label className="flex items-center gap-s3">
          Granskas senast
          <input
            type="date"
            className="nb-field h-auto py-s1"
            value=""
            onChange={(event) => onSetReview(event.target.value || null)}
          />
        </label>
      </div>
    );
  }

  return (
    <div className="mt-s4 flex flex-wrap items-center gap-s4 rounded-card bg-surface-2 px-s5 py-s3">
      {edition && (
        <span className="text-small text-ink">
          Fastställd utgåva <Num>{edition.edition}</Num>
          {edition.approved_by ? ` · ${edition.approved_by}` : ""} ·{" "}
          <Num>{edition.approved_at.slice(0, 10)}</Num>
        </span>
      )}
      {edition?.superseded && (
        <span className="rounded-control bg-warn-bg px-s3 py-s1 text-small text-warn-text">
          Utkastet har ändrats sedan utgåvan fastställdes
        </span>
      )}
      <label className="ml-auto flex items-center gap-s3 text-small text-muted">
        Granskas senast
        <input
          type="date"
          className={`nb-field h-auto py-s1 ${overdue ? "text-fail-text" : ""}`}
          value={reviewDate}
          disabled={!canEdit}
          onChange={(event) => onSetReview(event.target.value || null)}
        />
      </label>
      {overdue && (
        <span className="rounded-control bg-fail-bg px-s3 py-s1 text-small text-fail-text">
          Granskning försutten
        </span>
      )}
    </div>
  );
}
